import { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Container, Grid, Stack, Typography, Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import { NOT_AVAILABLE } from '../Global/Constants';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getMasterListType, saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { transformToSelectOptions } from '../../utils/CommonFunction';
import CustomSelect from '../../components/CustomSelect';
import CustomInput from '../../components/CustomInput';
import { BankOpsApi } from '../../utils/Apis';

const rejectionSchema = yup.object().shape({
  rejectedReasonTypeId: yup.string().nullable().required('Reject Reason is required'),
  comment: yup.string().required('Comments are required').max(500)
});

const sanctionSchema = yup.object().shape({
  amountSanctioned: yup
    .number()
    .typeError('Must be a number')
    .required('Amount Sanctioned is required'),
  processingFeePercentage: yup.number().notRequired().nullable(),
  totalProcessingFee: yup
    .number()
    .typeError('Must be a number')
    .required('Total Processing Fee is required'),
  bankWaiver: yup.number().typeError('Must be a number').required('Bank Waiver is required'),
  applicantShare: yup
    .number()
    .typeError('Must be a number')
    .required('Applicant Share is required')
    .min(0),
  companyShare: yup
    .number()
    .typeError('Must be a number')
    .required('Company Share is required')
    .min(0),
  processingFeeStatusId: yup.string().nullable().required('Status is required'),
  feeToBeDeductedFromInvoice: yup
    .number()
    .typeError('Must be a number')
    .required('Fee to be deducted is required'),
  payout: yup.number().typeError('Must be a number').required('Payout is required'),
  brokerAmount: yup.number().typeError('Must be a number').required('Broker Amount is required'),
  netRevenue: yup.number().typeError('Must be a number').required('Net Revenue is required')
});

export default function DecisionBankops() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state: rowId } = useLocation();
  const { LoginData } = useLoginSelector();
  const [masterOptions, setMasterOptions] = useState({ rejection: [], processing: [] });

  const isSanctioned = useMemo(
    () => rowId?.logInStatusName?.toLowerCase() === 'sanctioned',
    [rowId]
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(isSanctioned ? sanctionSchema : rejectionSchema)
  });

  const payout = watch('payout');
  const brokerAmount = watch('brokerAmount');

  useEffect(() => {
    const net = (Number(payout) || 0) - (Number(brokerAmount) || 0);
    setValue('netRevenue', net);
  }, [payout, brokerAmount, setValue]);

  // 🔥 Log errors for debugging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('❌ Form Validation Errors:', errors);
    }
  }, [errors]);

  useEffect(() => {
    (async () => {
      const [proc, rej] = await Promise.all([
        dispatch(getMasterListType('PROCESSING_FEE_STATUS_TYPE')),
        dispatch(getMasterListType('REJECTION_REASON_TYPE'))
      ]);
      setMasterOptions({
        processing: transformToSelectOptions(proc?.data?.masterTypes || [], 'typeName', 'typeId'),
        rejection: transformToSelectOptions(rej?.data?.masterTypes || [], 'typeName', 'typeId')
      });
    })();
  }, [dispatch]);

  const onSubmit = useCallback(
    async (data) => {
      console.log('🚀 Form Submitted Successfully with data:', data);
      const payload = {
        applicationId: rowId?.applicationId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        ...(isSanctioned
          ? {
              bankOpsDecisionInfoDisbursementRequestModel: {
                bankOpsDecisionInfoDisbursementDisbursementDetailsRequestModel: {
                  amountSanctioned: data.amountSanctioned
                },
                bankOpsDecisionInfoDisbursementProcessingFeeDetailsRequestModel: { ...data },
                bankOpsDecisionInfoDisbursementCommissionSharingRequestModel: {
                  leadOwnerId: rowId?.leadOwnerId,
                  leadOwner: rowId?.leadOwnerName
                }
              }
            }
          : {
              bankOpsDecisionInfoRejectionRequestModel: {
                bankOpsDecisionInfoRejectionRejectionDetailsRequestModel: { ...data }
              }
            })
      };
      const res = await dispatch(saveApiData(BankOpsApi.saveDecisionBankInfo, payload));
      if (res?.status === 'success') navigate('/bankops');
    },
    [LoginData, rowId, isSanctioned, dispatch, navigate]
  );

  return (
    <Page title="Partner Management System || Log In">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Application Closure Details" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <StatusBox status={rowId?.logInStatusName} />
          <form
            onSubmit={handleSubmit(onSubmit, (err) => {
              console.log('❌ handleSubmit rejected. Errors:', err);
            })}
          >
            {isSanctioned ? (
              <Box>
                <Typography
                  // style={{ marginTop: '30px' }}
                  variant="body1"
                  className="headingLineUp headingLineUpOut"
                >
                  Disbursement Details
                </Typography>
                <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomInput
                        name="amountSanctioned"
                        label="Amount Sanctioned"
                        control={control}
                        errors={errors}
                        isRequired
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Typography
                  // style={{ marginTop: '30px' }}
                  variant="body1"
                  className="headingLineUp headingLineUpOut"
                >
                  Processing Fee Details
                </Typography>
                <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                  <Grid container spacing={3}>
                    {[
                      // 'processingFeePercentage',
                      'totalProcessingFee',
                      'bankWaiver',
                      'applicantShare',
                      'companyShare'
                    ].map((f) => (
                      <Grid item xs={12} sm={6} md={4} key={f}>
                        <CustomInput
                          name={f}
                          label={f.replace(/([A-Z])/g, ' $1').trim()}
                          control={control}
                          errors={errors}
                          isRequired
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomSelect
                        name="processingFeeStatusId"
                        label="Status"
                        control={control}
                        errors={errors}
                        options={masterOptions.processing}
                        isRequired
                      />
                    </Grid>

                    {['feeToBeDeductedFromInvoice', 'payout', 'brokerAmount', 'netRevenue'].map(
                      (f) => (
                        <Grid item xs={12} sm={6} md={4} key={f}>
                          <CustomInput
                            name={f}
                            label={f.replace(/([A-Z])/g, ' $1').trim()}
                            control={control}
                            errors={errors}
                            isRequired
                            disabled={f === 'netRevenue'}
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography
                  style={{ marginTop: '30px' }}
                  variant="body1"
                  className="headingLineUp headingLineUpOut"
                >
                  Details
                </Typography>
                <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <CustomSelect
                        name="rejectedReasonTypeId"
                        label="Reject Reason"
                        control={control}
                        errors={errors}
                        options={masterOptions.rejection}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomInput
                        name="comment"
                        label="Comments"
                        type="textarea"
                        multiline
                        rows={2}
                        control={control}
                        errors={errors}
                        isRequired
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}
            <Box textAlign="right" mt={3}>
              <Button variant="contained" className="mainBtn" type="submit">
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </Container>
    </Page>
  );
}

function StatusBox({ status }) {
  return (
    <Box textAlign="right">
      <Box className="statusBox">
        <Typography variant="body2">Status:</Typography>
        <Typography variant="subtitle2" className="disbursedText">
          {status || NOT_AVAILABLE}
        </Typography>
      </Box>
    </Box>
  );
}
